// Sections 12, 18 and 19: the health-care (triage) policy, healers and their capacity, medicine
// on hand against what the sick need, visits to the healthy, and the quarantine. Props only.
import type { HealthCareOption } from '../../sim/api';
import type { HealthVM } from '../../viewModels/health';
import { oneDecimal } from '../format';
import { healthCareLabel, healthText } from '../text/en';

interface Props {
  vm: HealthVM;
  onPolicy: (option: HealthCareOption) => void;
}

export function CarePanel({ vm, onPolicy }: Props) {
  const q = vm.quarantine;
  return (
    <section class="health__section health__care-panel" aria-labelledby="health-care-title">
      <h3 id="health-care-title">{healthText.policy}</h3>
      <label class="health__policy">
        <span class="visually-hidden">{healthText.policy}</span>
        <select
          value={vm.policy}
          disabled={!vm.canEdit}
          onChange={(e) => onPolicy(e.currentTarget.value as HealthCareOption)}
        >
          {vm.policyOptions.map((o) => (
            <option key={o} value={o}>
              {healthCareLabel[o]}
            </option>
          ))}
        </select>
        {vm.policyPending && <span class="hint"> {healthText.pending}</span>}
      </label>
      <p class="hint">{healthText.policyHint}</p>

      <h4>{healthText.healers}</h4>
      {vm.healers.length === 0 ? (
        <p class="health__warning">{healthText.noHealers}</p>
      ) : (
        <ul class="health__healers">
          {vm.healers.map((h) => (
            <li key={h.id}>
              {h.name} <span class="num">{oneDecimal(h.slots)}</span>
              {!h.working && <span class="health__tag">{healthText.notWorking}</span>}
              {h.treatedLastSeason.length > 0 && (
                <span class="hint"> {healthText.treatedLast(h.treatedLastSeason.join(', '))}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p>{healthText.slots(oneDecimal(vm.slots), vm.capacity)}</p>
      {vm.visits > 0 && <p class="hint">{healthText.visits(vm.visits)}</p>}

      <h4>{healthText.medicine}</h4>
      <p>
        {healthText.medicineLine(
          oneDecimal(vm.medicine.onHand),
          oneDecimal(vm.medicine.capacity),
          oneDecimal(vm.medicine.demand),
        )}
      </p>
      <p class="hint">{healthText.medicineUse(oneDecimal(vm.medicine.plannedUse))}</p>
      {vm.medicine.short && <p class="health__warning">{healthText.medicineShort}</p>}

      <h4>{healthText.quarantine}</h4>
      <p>
        {q.active
          ? healthText.quarantineOn(
              q.isolated,
              q.untilTurn === null ? '' : healthText.quarantineUntil(q.untilTurn),
            )
          : healthText.quarantineOff}
      </p>
      {q.arrivalBoostUntilTurn !== null && (
        <p class="health__warning">{healthText.arrivalBoost(q.arrivalBoostUntilTurn)}</p>
      )}
    </section>
  );
}
